import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';

import { AttemptedPathService } from '@app/core/attempted-path.service';
import { LinksService } from '@app/core/links.service';
import { BreadcrumbCategory, SentryLevel } from '@app/core/metrics/sentry-metric.constants';
import { SentryMetricService } from '@app/core/metrics/sentry-metric.service';
import { LOGIN_WITH_AMAZON_MP_FLOW_VERSION } from '@app/core/mixpanel.constants';
import { LoginWithAmazonAnalyticsService } from '@app/login-with-amazon/login-with-amazon-analytics.service';
import { LoginWithAmazonCompletionModalComponent } from '@app/login-with-amazon/login-with-amazon-completion-modal/login-with-amazon-completion-modal.component';
import { RegistrationErrorCodes } from '@app/registration/registration-error/registration-error.component';
import { HORNBILL_ANALYTICS_FLOW_VERSION_PARAM_KEY } from '@app/shared/hornbill-params';
import { WindowService } from '@app/utils/window.service';

import { AuthService } from './auth.service';

export const UNAUTHORIZED_ERROR = 'unauthorized';
export const USER_BLOCKED_ERROR_DESCRIPTION = 'user is blocked';

@Injectable({
  providedIn: 'root',
})
export class Auth0ErrorGuardService implements CanActivate {
  private static readonly TYPE_NAME = 'Auth0ErrorGuardService';

  constructor(
    private authService: AuthService,
    private linksService: LinksService,
    private attemptedPathService: AttemptedPathService,
    private windowService: WindowService,
    private router: Router,
    private sentryMetricService: SentryMetricService,
    private loginWithAmazonAnalyticsService: LoginWithAmazonAnalyticsService,
  ) {}

  private readonly USER_IS_BLOCKED_ERROR_CODE = 'userIsBlocked';

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.router.getCurrentNavigation()?.extras.state?.skipAuth0ErrorGuard) {
      return true;
    }

    const urlQueryParams = this.windowService.getUrlQueryParams();
    const rawErrorDescription = urlQueryParams.get('error_description');
    const error = urlQueryParams.get('error');

    if (!rawErrorDescription) {
      return true;
    }

    let errorCode: string;

    type CustomAuth0ErrorDescription = Record<'claim_code' | 'errorCode', string>;
    let customAuth0ErrorDescription: CustomAuth0ErrorDescription = <CustomAuth0ErrorDescription>{};

    try {
      customAuth0ErrorDescription = JSON.parse(decodeURIComponent(rawErrorDescription));
      errorCode = customAuth0ErrorDescription.errorCode;
    } catch (e) {
      if (error === UNAUTHORIZED_ERROR && rawErrorDescription === USER_BLOCKED_ERROR_DESCRIPTION) {
        errorCode = this.USER_IS_BLOCKED_ERROR_CODE;
      } else {
        throw e;
      }
    }

    let queryParams: string;

    switch (errorCode) {
      case RegistrationErrorCodes.LoginWithAmazonEnrollmentDefault:
        // [LWA Hornbill] Track Auth0 enrollment error
        this.loginWithAmazonAnalyticsService.trackAuth0CallbackError(errorCode);
        const { claim_code: claimCode } = customAuth0ErrorDescription;
        // Sentry breadcrumb: Auth0 returned LWA enrollment error
        this.sentryMetricService.breadcrumb({
          category: BreadcrumbCategory.Lwa,
          message: 'Auth0 callback error: LoginWithAmazonEnrollmentDefault',
          level: SentryLevel.Error,
          data: { errorCode },
        });
        queryParams = new URLSearchParams({
          claim_code: claimCode,
          errorCode,
          [HORNBILL_ANALYTICS_FLOW_VERSION_PARAM_KEY]: LOGIN_WITH_AMAZON_MP_FLOW_VERSION,
        }).toString();

        this.authService.logout(`${this.linksService.registrationError}?${queryParams}`);
        break;
      case RegistrationErrorCodes.DuplicateExternalId:
        // [LWA Switch] Track Auth0 duplicate account error
        this.loginWithAmazonAnalyticsService.trackAuth0CallbackError(errorCode);
        // Sentry breadcrumb: Auth0 returned duplicate external ID error
        this.sentryMetricService.breadcrumb({
          category: BreadcrumbCategory.Lwa,
          message: 'Auth0 callback error: DuplicateExternalId',
          level: SentryLevel.Error,
          data: { errorCode },
        });
        queryParams = new URLSearchParams({
          errorCode,
          [HORNBILL_ANALYTICS_FLOW_VERSION_PARAM_KEY]: LOGIN_WITH_AMAZON_MP_FLOW_VERSION,
        }).toString();

        this.authService.logout(`${this.linksService.registrationError}?${queryParams}`);
        break;
      case 'userIsBlocked':
        const { url } = state;
        this.attemptedPathService.setAttemptedPath(state.url);
        this.authService.goLogin({
          path: url,
          customAuthorizationParams: { user_blocked: 'true' },
          trackingSource: Auth0ErrorGuardService.TYPE_NAME,
        });

        break;
      case RegistrationErrorCodes.FailedToLinkUser:
        // [LWA Switch] Track Auth0 link user failure
        this.loginWithAmazonAnalyticsService.trackAuth0CallbackError(errorCode);
        // Sentry breadcrumb: Auth0 returned failed to link user error
        this.sentryMetricService.breadcrumb({
          category: BreadcrumbCategory.Lwa,
          message: 'Auth0 callback error: FailedToLinkUser',
          level: SentryLevel.Error,
          data: { errorCode },
        });
        this.router.navigateByUrl(
          `${this.linksService.accountSecurity}?action=${LoginWithAmazonCompletionModalComponent.QUERY_PARAM_FAILURE_VALUE}`,
          {
            state: {
              skipAuth0ErrorGuard: true,
            },
          },
        );
        break;
      default:
        throw new Error(`Unknown error code: ${errorCode}`);
    }

    return false;
  }
}
